/**
 * Per-route SEO metadata (title, description, canonical, Open Graph).
 *
 * Why this exists: the site is a client-rendered SPA, so every route used to
 * serve the byte-identical `index.html` shell, complete with a hardcoded
 * `og:url` pointing at the home page. Google read that as "all 12 sitemap URLs
 * are the home page" and dropped 6 of them from the index with "Duplicate
 * without user-selected canonical".
 *
 * The fix has two halves and this module serves both:
 *   - In the browser, `applySeo` rewrites the tags on every navigation.
 *   - During prerendering, `collectSeo`/`takeCollectedSeo` hand the same
 *     metadata to scripts/prerender.mjs so it can bake real tags into each
 *     route's static HTML (see src/entry-prerender.tsx).
 */

export const SITE_NAME = 'Christopher Robert White'
export const SITE_URL = 'https://christopherrobertwhite.com'
export const DEFAULT_DESCRIPTION =
  'I build systems that combine machine learning and optimization to solve sourcing, allocation, and shipping decisions.'

export interface SeoMeta {
  title: string
  description: string
  canonical: string
}

/**
 * Canonical URL for a route. Always the apex host (never www) and never a
 * trailing slash except on the root, so `/resume` and `/resume/` collapse to
 * one canonical instead of reintroducing the duplicate we're fixing.
 */
export function canonicalUrl(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return `${SITE_URL}${trimmed === '' ? '/' : trimmed}`
}

export function buildSeo(
  pathname: string,
  title?: string,
  description?: string
): SeoMeta {
  return {
    title: title ? `${title} - ${SITE_NAME}` : SITE_NAME,
    description: description?.trim() || DEFAULT_DESCRIPTION,
    canonical: canonicalUrl(pathname),
  }
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Apply metadata to the live document. Browser only. */
export function applySeo(meta: SeoMeta) {
  document.title = meta.title
  upsertMeta('name', 'description', meta.description)
  upsertMeta('property', 'og:title', meta.title)
  upsertMeta('property', 'og:description', meta.description)
  upsertMeta('property', 'og:url', meta.canonical)

  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  )
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = meta.canonical
}

// --- Prerender bridge -------------------------------------------------------
// Module-scoped rather than a context because the prerenderer renders one route
// at a time, synchronously, in a fresh module registry per build.

let collected: SeoMeta | null = null

export function collectSeo(meta: SeoMeta) {
  collected = meta
}

/** Read and clear the metadata recorded during the last server render. */
export function takeCollectedSeo(): SeoMeta | null {
  const meta = collected
  collected = null
  return meta
}
